'use client';

/**
 * Renter Profile — address-select input for the Commute step.
 *
 * Work / School used to be free-text boxes, which meant we stored
 * whatever the user typed ("wrk", "midtown-ish") and could never do
 * anything with it. This turns them into single-select address
 * pickers backed by the same building-autocomplete endpoint the
 * Applicant Profile → Rental history address field uses, so the two
 * surfaces behave identically.
 *
 * Two deliberate differences from
 * `app/profile/application-profile/components/common/BuildingAddressAutocomplete`:
 *
 *   1. No `getSearchItemApi` round-trip on select. Rental history
 *      needs the resolved `buildingSlug` to link the tenancy to a
 *      building; commute preferences store a plain string
 *      (`preferred_work_location`), so the suggestion's own address
 *      is already the final value. Skipping the resolve halves the
 *      taps-to-saved latency and, more importantly, lets the user
 *      pick an office or campus that isn't an openigloo building.
 *   2. Preferences pill styling instead of the `Input` component,
 *      so the field still matches the chips around it.
 *
 * SELECTION-ONLY. Text the user typed but never picked from the
 * dropdown is discarded on blur and the field snaps back to the last
 * committed value. The single exception is an emptied box, which
 * commits `''` — that's the only way to remove a saved location.
 */

import React from 'react';
import Popover from '@/components/ui/Popover';
import ListCell, { ListCellSkeleton } from '@/components/ui/ListCell';
import { IconDeleteCircle, IconLocationPin } from '@repo/svgs';
import useBuildingAutocomplete from '@/features/buildings/hooks/useBuildingAutocomplete';
import { cn } from '@/lib/cn';

/** Matches the hook default + the rental-history field. */
const MIN_QUERY_LENGTH = 4;
const DEBOUNCE_MS = 300;

export interface PreferenceAddressAutocompleteProps {
  /** Last committed address. `''` means "not set". */
  value: string;
  /**
   * Fired ONLY on a real commit — a suggestion pick, or an emptied
   * field on blur. Never fires for in-progress typing, so callers can
   * treat every call as a value worth persisting.
   */
  onCommit: (address: string) => void;
  placeholder?: string;
  /** Pill classes from the calling surface, so tokens stay in one place. */
  className?: string;
  /** Analytics tap handler. `onClick`, not `onFocus` — see call sites. */
  onTap?: (() => void) | undefined;
  'aria-label'?: string;
}

export function PreferenceAddressAutocomplete({
  value,
  onCommit,
  placeholder = 'Search address or building',
  className,
  onTap,
  'aria-label': ariaLabel,
}: PreferenceAddressAutocompleteProps) {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const [text, setText] = React.useState(value);
  const [open, setOpen] = React.useState(false);

  /**
   * Last value we handed to `onCommit` (or received as `value`).
   * Kept in a ref rather than derived from the `value` prop because
   * blur has to compare against it synchronously, before the parent
   * has re-rendered with the new prop.
   */
  const committedRef = React.useRef(value);

  /**
   * Gates the dropdown to genuine keystrokes. Without it, every
   * programmatic `setText` — seeding from the server, snapping back
   * on blur, echoing the picked suggestion — would look like a query
   * and pop the menu open again.
   */
  const typingRef = React.useRef(false);

  React.useEffect(() => {
    committedRef.current = value;
    typingRef.current = false;
    setText(value);
    setOpen(false);
  }, [value]);

  const { suggestions, isLoading } = useBuildingAutocomplete(text, {
    minLength: MIN_QUERY_LENGTH,
    debounceMs: DEBOUNCE_MS,
  });

  React.useEffect(() => {
    if (!typingRef.current) return;
    setOpen(text.trim().length >= MIN_QUERY_LENGTH);
  }, [text]);

  const handleChange = (next: string) => {
    typingRef.current = true;
    setText(next);
  };

  const handleSelect = (address: string) => {
    typingRef.current = false;
    committedRef.current = address;
    setText(address);
    setOpen(false);
    onCommit(address);
  };

  const handleBlur = () => {
    const typed = text.trim();
    if (typed === committedRef.current) return;

    typingRef.current = false;
    setOpen(false);

    // Emptying the field is the one free-text edit that counts — it's
    // how you remove a saved location.
    if (typed === '') {
      committedRef.current = '';
      onCommit('');
      setText('');
      return;
    }

    // Anything else was typed but never picked, so it isn't a real
    // address. Restore what's actually saved.
    setText(committedRef.current);
  };

  const handleClear = () => {
    typingRef.current = false;
    setText('');
    setOpen(false);

    // Skip the write when nothing was saved yet — clearing a
    // half-typed query shouldn't cost a request.
    if (committedRef.current !== '') {
      committedRef.current = '';
      onCommit('');
    }
  };

  const showMenu = text.trim().length >= MIN_QUERY_LENGTH;
  const showClear = text.length > 0;

  return (
    <>
      <div ref={anchorRef} className="relative">
        <input
          type="text"
          // Extra right padding keeps long addresses from running under
          // the clear button.
          className={cn(className, showClear && 'pr-11')}
          placeholder={placeholder}
          value={text}
          onChange={e => handleChange(e.target.value)}
          onBlur={handleBlur}
          onClick={onTap}
          // Browser autofill would drop an unvalidated string straight
          // into a selection-only field.
          autoComplete="off"
          {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        />
        {showClear && (
          <button
            type="button"
            aria-label="Clear address"
            // pointerDown + preventDefault, not onClick: a click would
            // let the input blur first, and `handleBlur` would restore
            // the committed value a beat before we cleared it — two
            // writes for one gesture.
            onPointerDown={e => {
              e.preventDefault();
              handleClear();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full p-2 cursor-pointer"
          >
            <IconDeleteCircle className="icon-secondary" width={16} height={16} />
          </button>
        )}
      </div>

      <Popover
        open={open}
        onOpenChange={o => setOpen(o)}
        anchorRef={anchorRef}
        matchAnchorWidth={true}
        // Sits above the desktop Modal overlay (z-50) and the mobile
        // sheet content (z-50, or z-70 when nested).
        className="!z-[80]"
      >
        {showMenu && (
          <div className="p-4 rounded-4xl bg-primary border border-tertiary shadow-lg max-h-72 overflow-y-auto">
            {isLoading ? (
              <div>
                {[1, 2, 3].map(i => (
                  <ListCellSkeleton key={i} />
                ))}
              </div>
            ) : suggestions.length > 0 ? (
              suggestions.map(s => (
                <ListCell
                  key={s.placeId}
                  title={s.address}
                  {...(s.borough ? { subtitle: s.borough } : {})}
                  leadingIcon={<IconLocationPin className="w-6 h-6 icon-secondary" />}
                  onPointerDown={e => {
                    // preventDefault stops the input from blurring
                    // before the tap lands — blur would snap the text
                    // back to the committed value and swallow the
                    // selection.
                    //
                    // stopPropagation does double duty: React's
                    // SyntheticEvent forwards it to the native event,
                    // and since React binds this portal's listener on
                    // `document.body`, that halts the event one level
                    // below `document` — which is where vaul/Radix
                    // dismiss layers listen. Without it, tapping a
                    // suggestion inside the wizard's mobile sheet
                    // would also close the sheet.
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(s.address);
                  }}
                />
              ))
            ) : (
              <div className="flex flex-col items-center gap-2 py-2 text-center">
                <p className="text-body-1-high text-primary">No addresses found</p>
                <p className="text-body-2-normal text-secondary">
                  Try a street address (e.g., 200 Water St) or a building name (e.g., Silver
                  Towers).
                </p>
              </div>
            )}
          </div>
        )}
      </Popover>
    </>
  );
}

export default PreferenceAddressAutocomplete;
